import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  HStack,
  Input,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { ClipboardCopy, Search, UserRound } from 'lucide-react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import Drawer, {
  DrawerBody,
  DrawerCloseTrigger,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/Drawer';
import SearchableSelect, { SearchableSelectOption } from '@/components/ui/SearchableSelect';
import { toaster } from '@/components/ui/Toaster';
import withHashDrawer from '@/components/ui/withHashDrawer';
import {
  User,
  useCopyUserConditionsMutation,
  useGetUserConditionsQuery,
  useGetUserByIdQuery,
  useLazyGetUsersQuery,
} from '../api';
import { copyConditionsSchema, CopyConditionsFormValues } from './CopyConditionsModal.schema';

interface CopyConditionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId?: number;
  onSuccess?: () => void;
}

const userLabel = (user: User) => {
  const name = `${user.first_name} ${user.last_name}`.trim();
  return name ? `${name} — ${user.email}` : user.email;
};

const CopyConditionsModal: React.FC<CopyConditionsModalProps> = ({
  isOpen,
  onClose,
  entityId,
  onSuccess,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(isOpen);
  const [sourceUser, setSourceUser] = useState<User | null>(null);
  const [conditionSearch, setConditionSearch] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const sourceUsersById = useRef(new Map<number, User>());
  const [searchUsersApi] = useLazyGetUsersQuery();
  const [copyConditions, { isLoading: isCopying }] = useCopyUserConditionsMutation();
  const { data: destinationUser } = useGetUserByIdQuery(entityId!, { skip: !entityId });
  const {
    data: sourceConditions = [],
    isFetching: isLoadingConditions,
    isError: isConditionsError,
  } = useGetUserConditionsQuery(sourceUser?.id as number, {
    skip: !sourceUser?.id,
  });

  const form = useForm<CopyConditionsFormValues>({
    resolver: zodResolver(copyConditionsSchema),
    defaultValues: {
      source_user_id: 0,
      condition_ids: [],
    },
  });
  const selectedConditionIds = useWatch({ control: form.control, name: 'condition_ids' });

  useEffect(() => {
    if (isOpen) {
      setIsDrawerOpen(true);
    }
  }, [isOpen]);

  const filteredConditions = useMemo(() => {
    const query = conditionSearch.trim().toLowerCase();
    if (!query) return sourceConditions;

    return sourceConditions.filter((condition) =>
      condition.name.toLowerCase().includes(query)
      || condition.description?.toLowerCase().includes(query)
    );
  }, [conditionSearch, sourceConditions]);

  const searchUsers = useCallback(async (query: string): Promise<SearchableSelectOption[]> => {
    const response = await searchUsersApi({
      search: query.trim() || undefined,
      per_page: 50,
    }).unwrap();

    const users = response.data.filter((user) => user.id !== entityId && !!user.profile?.id);
    sourceUsersById.current = new Map(users.map((user) => [user.id, user]));

    return users.map((user) => ({ value: user.id.toString(), label: userLabel(user) }));
  }, [entityId, searchUsersApi]);

  const selectSourceUser = useCallback((option: SearchableSelectOption) => {
    const userId = Number(option.value);
    const user = sourceUsersById.current.get(userId);
    form.setValue('source_user_id', userId, { shouldValidate: true });
    form.setValue('condition_ids', [], { shouldValidate: false });
    form.clearErrors(['source_user_id', 'condition_ids']);
    setServerError(null);
    setConditionSearch('');

    if (user) {
      setSourceUser(user);
    } else {
      setSourceUser(null);
      form.setError('source_user_id', { message: 'Could not load the selected user' });
    }
  }, [form]);

  const resetAndClose = useCallback(() => {
    form.reset();
    setSourceUser(null);
    setConditionSearch('');
    setServerError(null);
    setIsDrawerOpen(false);
  }, [form]);

  const handleExitComplete = useCallback(() => {
    if (!isDrawerOpen) {
      onClose();
    }
  }, [isDrawerOpen, onClose]);

  const submitForm = form.handleSubmit(async (values) => {
    if (!entityId) return;

    setServerError(null);
    try {
      const result = await copyConditions({ targetUserId: entityId, data: values }).unwrap();
      toaster.create({
        type: 'success',
        title: 'Conditions copied',
        description: `${result.copied_count} condition${result.copied_count === 1 ? '' : 's'} copied successfully.`,
      });
      onSuccess?.();
      resetAndClose();
    } catch (error: unknown) {
      const apiError = error as {
        data?: { message?: string; errors?: Record<string, string[]> };
      };
      const validationMessage = apiError.data?.errors
        ? Object.values(apiError.data.errors).flat().find((message) => typeof message === 'string')
        : null;
      setServerError(
        validationMessage || apiError.data?.message || 'Failed to copy conditions'
      );
    }
  });

  const destinationName = destinationUser
    ? `${destinationUser.first_name} ${destinationUser.last_name}`.trim()
    : '';

  return (
    <Drawer
      isOpen={isDrawerOpen}
      onClose={resetAndClose}
      onExitComplete={handleExitComplete}
      size="md"
    >
      <DrawerHeader>
        <DrawerTitle color="brand.700" _dark={{ color: 'brand.300' }}>
          <HStack>
            <ClipboardCopy size={20} />
            <Text>Copy conditions</Text>
          </HStack>
        </DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>

      <DrawerBody>
        <form id="copy-conditions-form" onSubmit={submitForm}>
          <VStack gap={6} align="stretch">
            <Box
              p={4}
              borderWidth={1}
              borderRadius="md"
              borderColor="gray.200"
              bg="gray.50"
              _dark={{ borderColor: 'gray.600', bg: 'gray.800' }}
            >
              <HStack align="start">
                <UserRound size={18} />
                <Box>
                  <Text fontSize="sm" color="gray.600">Copying to</Text>
                  <Text fontWeight="semibold">{destinationName || destinationUser?.email || 'Loading user...'}</Text>
                  {destinationName && <Text fontSize="sm" color="gray.600">{destinationUser?.email}</Text>}
                </Box>
              </HStack>
            </Box>

            <Box>
              <SearchableSelect
                label="Copy from user"
                onSearch={searchUsers}
                onOptionSelect={selectSourceUser}
                onChange={(value) => {
                  if (!value) {
                    setSourceUser(null);
                    form.setValue('source_user_id', 0);
                    form.setValue('condition_ids', []);
                    setConditionSearch('');
                  }
                }}
                placeholder="Search by name or email..."
                noResultsText="No users with profiles found"
                minSearchLength={0}
              />
              {form.formState.errors.source_user_id?.message && (
                <Text color="red.600" fontSize="sm" mt={1}>
                  {form.formState.errors.source_user_id.message}
                </Text>
              )}
            </Box>

            {sourceUser && (
              <Box>
                <HStack justify="space-between" mb={3}>
                  <Text fontSize="sm" fontWeight="medium">Select conditions</Text>
                  {sourceConditions.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      onClick={() => form.setValue(
                        'condition_ids',
                        selectedConditionIds.length === sourceConditions.length
                          ? []
                          : sourceConditions.map((condition) => condition.id),
                        { shouldValidate: true }
                      )}
                    >
                      {selectedConditionIds.length === sourceConditions.length ? 'Clear all' : 'Select all'}
                    </Button>
                  )}
                </HStack>

                {sourceConditions.length > 0 && (
                  <Box position="relative" mb={3}>
                    <Box position="absolute" left={3} top="50%" transform="translateY(-50%)" color="gray.500">
                      <Search size={16} />
                    </Box>
                    <Input
                      value={conditionSearch}
                      onChange={(event) => setConditionSearch(event.target.value)}
                      placeholder="Search conditions..."
                      pl={10}
                    />
                  </Box>
                )}

                {isLoadingConditions ? (
                  <HStack py={6} justify="center"><Spinner size="sm" /><Text>Loading conditions...</Text></HStack>
                ) : isConditionsError ? (
                  <Text color="red.600" fontSize="sm">Could not load this user's conditions.</Text>
                ) : sourceConditions.length === 0 ? (
                  <Text color="gray.600" fontSize="sm">This user has no conditions to copy.</Text>
                ) : filteredConditions.length === 0 ? (
                  <Text color="gray.600" fontSize="sm">No conditions match your search.</Text>
                ) : (
                  <Controller
                    control={form.control}
                    name="condition_ids"
                    render={({ field }) => (
                      <VStack align="stretch" gap={2} maxH="20rem" overflowY="auto">
                        {filteredConditions.map((condition) => (
                          <Checkbox.Root
                            key={condition.id}
                            checked={field.value.includes(condition.id)}
                            onCheckedChange={({ checked }) => {
                              field.onChange(
                                checked
                                  ? [...field.value, condition.id]
                                  : field.value.filter((id) => id !== condition.id)
                              );
                            }}
                          >
                            <Checkbox.HiddenInput />
                            <Checkbox.Control />
                            <Checkbox.Label>
                              <Text fontWeight="medium">{condition.name}</Text>
                              {condition.description && (
                                <Text fontSize="xs" color="gray.600" lineClamp={2}>{condition.description}</Text>
                              )}
                            </Checkbox.Label>
                          </Checkbox.Root>
                        ))}
                      </VStack>
                    )}
                  />
                )}
                {form.formState.errors.condition_ids?.message && (
                  <Text color="red.600" fontSize="sm" mt={1}>
                    {form.formState.errors.condition_ids.message}
                  </Text>
                )}
              </Box>
            )}

            {serverError && <Text color="red.600" fontSize="sm">{serverError}</Text>}
          </VStack>
        </form>
      </DrawerBody>

      <DrawerFooter>
        <HStack gap={3}>
          <Button variant="outline" onClick={resetAndClose} disabled={isCopying}>Cancel</Button>
          <Button
            type="submit"
            form="copy-conditions-form"
            colorPalette="brand"
            loading={isCopying}
            disabled={isCopying || !sourceUser || selectedConditionIds.length === 0}
          >
            {selectedConditionIds.length > 0
              ? `Copy ${selectedConditionIds.length} condition${selectedConditionIds.length === 1 ? '' : 's'}`
              : 'Copy conditions'}
          </Button>
        </HStack>
      </DrawerFooter>
    </Drawer>
  );
};

export default withHashDrawer(CopyConditionsModal, { action: 'copy-conditions' });
